import React, { useState, useEffect } from 'react';
import { SketchPicker } from 'react-color';
import { X, UserPlus, User, MapPin, Activity } from 'lucide-react';
import { BENEFICIARY_STATUS, STATUS_LABELS } from '../constants/beneficiaryStatuses';

const extendedColorPalette = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F06292', '#AED581',
  '#FFD54F', '#7986CB', '#9575CD', '#4DB6AC', '#FFF176', '#FF8A65', '#A1887F',
  '#90A4AE', '#FFAB91', '#F48FB1', '#81C784', '#64B5F6', '#BA68C8', '#4DD0E1',
  '#DCE775', '#FFB74D', '#E57373', '#F06292', '#BA68C8', '#9575CD', '#7986CB',
  '#64B5F6', '#4FC3F7', '#4DD0E1', '#4DB6AC', '#81C784', '#AED581', '#DCE775',
  '#FFF176', '#FFD54F', '#FFB74D', '#FF8A65', '#A1887F', '#90A4AE', '#D1C4E9',
  '#B39DDB', '#9575CD', '#7E57C2', '#673AB7', '#5C6BC0', '#3F51B5', '#3949AB',
  '#303F9F', '#283593', '#1A237E', '#1976D2', '#1565C0', '#0D47A1', '#0277BD',
  '#01579B', '#00838F', '#006064', '#004D40', '#00695C', '#004C3F', '#00BFA5',
  '#00ACC1', '#0097A7', '#00838F', '#006064', '#FF6B6B', '#4ECDC4', '#45B7D1',
  '#FFD54F', '#7986CB', '#9575CD', '#4DB6AC', '#FFF176', '#FF8A65', '#A1887F',
  '#90A4AE', '#FFAB91', '#F48FB1', '#81C784', '#64B5F6', '#BA68C8', '#4DD0E1',
];

const ClientForm = (props) => {
  ClientForm.displayName = 'ClientForm';
  // Protection contre les props null
  const safeProps = props || {};
  const { client = null, usedColors = [], onSave = () => { }, onCancel = () => { } } = safeProps;

  console.log('ClientForm received props:', { client, usedColors });

  // Fonction utilitaire pour extraire les champs d'adresse
  const getAddressFields = (addr) => {
    if (!addr) return { street: '', postalCode: '', city: '', country: 'France' };
    if (typeof addr === 'string') return { street: addr, postalCode: '', city: '', country: 'France' };
    return {
      street: addr.street || '',
      postalCode: addr.postalCode || '',
      city: addr.city || '',
      country: addr.country || 'France'
    };
  };

  const initialAddress = getAddressFields(client?.address);

  // Fonction utilitaire pour déterminer le statut initial
  const getInitialStatus = (c) => {
    if (!c) return BENEFICIARY_STATUS.ACTIVE;
    if (c.status) return c.status;
    if (c.isArchived) return BENEFICIARY_STATUS.ARCHIVED;
    if (c.isActive === false) return BENEFICIARY_STATUS.INACTIVE;
    return BENEFICIARY_STATUS.ACTIVE;
  };

  const [clientData, setClientData] = useState({
    firstName: client?.firstName || '',
    lastName: client?.lastName || '',
    email: client?.email || '',
    phoneNumber: client?.phoneNumber || '',
    // Adresse décomposée
    street: initialAddress.street,
    postalCode: initialAddress.postalCode,
    city: initialAddress.city,
    country: initialAddress.country,

    addressNumber: client?.addressNumber || '', // Gardé pour rétro-compatibilité ou complément
    birthDate: client?.birthDate || '',
    status: getInitialStatus(client),
    color: client?.color || ''
  });

  const [errors, setErrors] = useState({});

  const [colorPickerVisible, setColorPickerVisible] = useState(false);

  // Filtrer les couleurs disponibles en excluant les couleurs déjà utilisées par d'autres clients
  const availableColors = extendedColorPalette.filter(color => !usedColors.includes(color));

  useEffect(() => {
    if (!clientData.color && availableColors.length > 0) {
      // Si aucune couleur n'est définie et des couleurs sont disponibles, en choisir une par défaut
      setClientData(prev => ({ ...prev, color: availableColors[0] }));
    }
  }, [availableColors, clientData.color]);

  const handleColorChange = (color) => {
    setClientData(prev => ({ ...prev, color: color.hex }));
  };

  const validate = () => {
    const newErrors = {};
    if (!clientData.firstName || !clientData.firstName.trim()) newErrors.firstName = "Le prénom est requis";
    if (!clientData.lastName || !clientData.lastName.trim()) newErrors.lastName = "Le nom est requis";

    if (clientData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (clientData.phoneNumber && !/^[\d\+\-\s\(\)]{4,20}$/.test(clientData.phoneNumber)) {
      newErrors.phoneNumber = "Format de téléphone invalide";
    }

    if (clientData.postalCode && !/^\d{5}$/.test(clientData.postalCode)) {
      newErrors.postalCode = "Le code postal doit contenir 5 chiffres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSave = () => {
    if (!validate()) return;

    // Reconstruire l'objet adresse et nettoyer les données
    const finalData = {
      ...clientData,
      address: {
        street: clientData.street,
        postalCode: clientData.postalCode,
        city: clientData.city,
        country: clientData.country
      },
      // Assurer la cohérence avec les anciens champs pour rétro-compatibilité si nécessaire
      isActive: clientData.status === BENEFICIARY_STATUS.ACTIVE || clientData.status === BENEFICIARY_STATUS.PENDING,
      isArchived: clientData.status === BENEFICIARY_STATUS.ARCHIVED
    };

    // Supprimer les champs plats temporaires
    delete finalData.street;
    delete finalData.postalCode;
    delete finalData.city;
    delete finalData.country;

    onSave(finalData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        {/* En-tête */}
        <div className="p-6 border-b flex justify-between items-center bg-blue-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {client ? 'Modifier le bénéficiaire' : 'Ajouter un bénéficiaire'}
            </h2>
            <p className="text-gray-600 mt-1">
              {client ? 'Modifiez les informations du bénéficiaire' : 'Ajoutez un nouveau bénéficiaire au système OMAD'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
            {/* Informations personnelles */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3 flex items-center">
                <UserPlus size={18} className="mr-2" />
                1. Informations personnelles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={clientData.firstName}
                    onChange={handleInputChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Prénom du bénéficiaire"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={clientData.lastName}
                    onChange={handleInputChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Nom du bénéficiaire"
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de naissance
                  </label>
                  <input
                    type="date"
                    name="birthDate"
                    value={clientData.birthDate}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Couleur d'identification
                  </label>
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-full border-2 border-gray-300 cursor-pointer shadow-sm"
                      style={{ backgroundColor: clientData.color }}
                      onClick={() => setColorPickerVisible(!colorPickerVisible)}
                    />
                    <input
                      type="text"
                      name="color"
                      value={clientData.color}
                      onChange={handleInputChange}
                      placeholder="#4FC3F7"
                      className="w-32 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  {colorPickerVisible && (
                    <div className="mt-2">
                      <SketchPicker
                        color={clientData.color}
                        colors={availableColors}
                        onChange={handleColorChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Coordonnées */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3 flex items-center">
                <User size={18} className="mr-2" />
                2. Coordonnées
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={clientData.email}
                    onChange={handleInputChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="email@exemple.com"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={clientData.phoneNumber}
                    onChange={handleInputChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="+33 1 23 45 67 89"
                  />
                  {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
                </div>
              </div>
            </div>

            {/* Adresse structurée */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3 flex items-center">
                <MapPin size={18} className="mr-2" />
                3. Adresse complète
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rue / Voie
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={clientData.street}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ex: 12 rue de la Paix"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complément
                  </label>
                  <input
                    type="text"
                    name="addressNumber"
                    value={clientData.addressNumber}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Apt, Étage..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code Postal
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={clientData.postalCode}
                    onChange={handleInputChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.postalCode ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="75000"
                  />
                  {errors.postalCode && <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={clientData.city}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Paris"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pays
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={clientData.country}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="France"
                  />
                </div>
              </div>
            </div>

            {/* Statut complet */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3 flex items-center">
                <Activity size={18} className="mr-2" />
                4. Statut du dossier
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  État actuel du bénéficiaire
                </label>
                <select
                  name="status"
                  value={clientData.status}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                >
                  {Object.values(BENEFICIARY_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  Le statut détermine la visibilité du bénéficiaire et les actions autorisées (facturation, planning, etc.)
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <UserPlus size={16} className="mr-2" />
                {client ? 'Modifier' : 'Créer'} le bénéficiaire
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientForm;
